import {
  OidcClient,
  SigninResponse,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts'

import { env } from '@/config/env'

type UserManagerWithInternals = UserManager & {
  _client: OidcClient
  _buildUser: (
    signinResponse: SigninResponse,
    verifySub?: string
  ) => Promise<User>
}

/**
 * oidc-client-ts {@link UserManager.signinResourceOwnerCredentials} only forwards
 * `settings.extraTokenParams` and ignores per-call `extraTokenParams` (see library source).
 * Keycloak OTP on the password grant requires `otp` on the token request — merge here.
 */
export async function signinResourceOwnerCredentialsWithMergedExtra(
  um: UserManager,
  args: {
    username: string
    password: string
    skipUserInfo?: boolean
    extraTokenParams?: Record<string, unknown>
  }
): Promise<User> {
  const internal = um as UserManagerWithInternals
  const signinResponse =
    await internal._client.processResourceOwnerPasswordCredentials({
      username: args.username,
      password: args.password,
      skipUserInfo: args.skipUserInfo ?? false,
      extraTokenParams: {
        ...um.settings.extraTokenParams,
        ...args.extraTokenParams,
      },
    })
  return internal._buildUser.call(um, signinResponse)
}

function buildSettings(): UserManagerSettings {
  const url = env.KEYCLOAK_URL as string
  const realm = env.KEYCLOAK_REALM as string
  const clientId = env.KEYCLOAK_CLIENT_ID as string

  if (typeof window === 'undefined') {
    throw new Error('keycloakUserManager must be used in the browser')
  }

  const origin = window.location.origin

  return {
    authority: `${url.replace(/\/$/, '')}/realms/${realm}`,
    client_id: clientId,
    /** Default is sessionStorage → OIDC user is per-tab; localStorage is origin-wide (all tabs). */
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: 'openid profile email',
    automaticSilentRenew: true,
    monitorSession: false,
    /** Helps some IdPs (including Keycloak) accept silent renew via iframe. */
    includeIdTokenInSilentRenew: true,
    /** Needed so the silent-renew iframe can postMessage the parent (avoids ErrorTimeout). */
    iframeNotifyParentOrigin: origin,
    /** Keycloak / slow network: the default 10s often times out. */
    silentRequestTimeoutInSeconds: 30,
  }
}

let manager: UserManager | null = null

export function getKeycloakUserManager(): UserManager {
  if (!manager) {
    manager = new UserManager(buildSettings())
  }

  return manager
}
